// ---------------------------------------------------------------------------
// Spinner, Skeleton, EmptyState and SaveStatus, ported from the design system
// (R10)
//
// spinner(): the ring Button uses, on its own, as role status with its label
// for screen readers (visually hidden, or shown with showLabel); the ring is
// aria-hidden. sm 14px, md 20px, lg 32px; center centres it in a padded
// block. decorative leaves only the ring, hidden from screen readers.
// skeleton(): grey shapes where content is about to be: text lines (the last
// shorter), a block, DataTable's header and rows (the first column wider, the
// last right-aligned past two columns, compact rows), or a MetricTile. Hidden
// from screen readers; with a label the shapes alone are hidden and the label
// is hidden text. A width or height that is a number is in px. Both hold no
// state in the system, so they are template functions with the system's prop
// names (className); nothing is renamed.
//
// <sdr-empty-state>: what is missing, why, and the one thing to do next. A
// light-DOM Lit element, because the system's holds state and effects:
//   - no-results is announced politely: its role status goes in empty, and
//     150 ms after it appears (or after its title or body text changes) it
//     receives the title and body as sentences, as a new node each time, and
//     empties again after 7 s. Only text and numbers count as a change, as in
//     the system's effect; the words are read from the page when they go out.
//   - error is role alert; with onRetry and no action it shows Try again
//     (secondary), which spins and waits while `retrying`. If the focus was
//     in the state when Try again was pressed, it waits on the state (which
//     takes focus, tabindex -1) while the button is busy, and goes back to
//     Try again when the retry ends and the state is still there.
//   - the title is an h3 (level 2 to 6, clamped as the system does).
//   - no icon: an empty state is its title, body and one action (decision
//     I7; the system dropped its icon in S18).
// <sdr-save-status>: one quiet line beside a form's title. Also an element:
//   - saved shows the check and the time, relative ("just now", "4 min ago",
//     "at 14:05", "on Oct 27, 14:05") and kept current every 30 s while saved,
//     the full date and time on hover; saving the 12px ring; unsaved the dot;
//     failed the alert and the error, with Retry (sm) when onRetry is given.
//   - only the outcome is announced, politely: "Saved" when the state turns
//     saved, the failure's words when it turns failed or its words change
//     while failed. Each announcement is a new node, so the same failure
//     twice is read twice; it empties again after 5 s. Mounting already saved
//     or failed announces nothing.
//   - Retry goes away while saving: if the focus was in the status when Retry
//     was pressed, it waits on the status (tabindex -1) and goes back to Retry
//     if the save failed again.
//
// Callbacks get what the system's get: onRetry() nothing (the system wraps
// the button's click). A focus counts as lost when it is on the page or on a
// disabled control (Chrome takes it off a control that turns disabled; this
// is the system's fbFocusLost, kept here: helpers.ts has none). The system's
// asDate is private to helpers.ts, so it is repeated here too.
//
// Timers and the interval stop when the element leaves the page; when it
// comes back they start again as at a first mount (an announcement in flight
// is dropped), as React would mount it anew.
//
// Renamed, because the elements would otherwise shadow their own HTML
// attributes: EmptyState title → heading, children → content, className →
// emptyClass; SaveStatus className → saveClass.
// ---------------------------------------------------------------------------

import { html, LitElement, nothing, type PropertyValues, type TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { keyed } from 'lit/directives/keyed.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { html as staticHtml, unsafeStatic } from 'lit/static-html.js';

import { button } from './button.js';
import { cx, format, noBox, node, num, present } from './helpers.js';
import { icon } from './icons.js';

const range = (n: number): number[] => {
  const a: number[] = [];
  for (let i = 0; i < n; i++) a.push(i);
  return a;
};

/** A length as the system's fbDim gives it: a number is in px, anything else as it is. */
const dim = (v: number | string | null | undefined) => (v == null ? undefined : typeof v === 'number' ? `${v}px` : v);

/** One style declaration as React writes it: nothing for an empty value, the value trimmed. */
const declaration = (property: string, value: unknown) =>
  value == null || typeof value === 'boolean' || value === '' ? undefined : `${property}:${String(value).trim()}`;

/** The focus was on a control that just went away or went disabled: on the page, or on the disabled control. */
function focusLost(): boolean {
  const a = document.activeElement as (Element & { disabled?: boolean }) | null;
  return !a || a === document.body || !!a.disabled;
}

const asDate = (d: Date | string | number) => (d instanceof Date ? d : new Date(d));

// ---- Spinner ----

export interface SpinnerProps {
  /** sm 14px (in rows, beside 13px text), md 20px (beside body text), lg 32px (a panel or page that loads). */
  size?: 'sm' | 'md' | 'lg';
  /** What is loading, read by screen readers ("Loading replies"). Default "Loading". */
  label?: string;
  /** Show the label beside the ring (below it with center). */
  showLabel?: boolean;
  /** Centre it in a padded block: the whole body of a panel while it loads. */
  center?: boolean;
  /** Only the ring, hidden from screen readers, when text next to it already says it is loading. */
  decorative?: boolean;
  className?: string;
}

/** A loading ring with its name for screen readers (role status). */
export function spinner(p: SpinnerProps = {}): TemplateResult {
  const size = p.size || 'sm';
  const cls = cx('sdr-loader', `sdr-loader--${size}`, p.center && 'sdr-loader--center', p.className);
  const ring = html`<span class=${cx('sdr-spinner', size !== 'sm' && `sdr-spinner--${size}`)} aria-hidden="true"></span>`;
  if (p.decorative) return html`<span class=${cls} aria-hidden="true">${ring}</span>`;
  return html`<span class=${cls} role="status">${ring}<span class=${p.showLabel ? 'sdr-loader__label' : 'sdr-visually-hidden'}>${node(p.label || 'Loading')}</span></span>`;
}

// ---- Skeleton ----

const LINE = ['100%', '94%', '97%', '89%', '92%'];
const CELL0 = ['72%', '58%', '66%', '50%', '62%'];
const CELL = ['48%', '36%', '54%', '42%'];

const bone = (width: string | undefined, kind?: string) =>
  html`<span class=${cx('sdr-skeleton__bone', kind && `sdr-skeleton__bone--${kind}`)} style=${ifDefined(width ? declaration('width', width) : undefined)}></span>`;

export interface SkeletonProps {
  /** text: lines of body text; block: a rectangle; table: DataTable's header and rows; metric: a MetricTile. */
  variant?: 'text' | 'block' | 'table' | 'metric';
  /** text: number of lines (default 3; the last is shorter). */
  lines?: number;
  /** Width of the whole skeleton, as CSS or px. */
  width?: number | string;
  /** block: height as CSS or px (default 120px). */
  height?: number | string;
  /** table: body rows (default 5) and columns (default 4). */
  rows?: number;
  columns?: number;
  /** table: row-default (52px) or row-compact (40px), as in DataTable. */
  density?: 'default' | 'compact';
  /** What is loading, as hidden text for screen readers. Without it the whole skeleton is aria-hidden. */
  label?: string;
  className?: string;
}

/** Grey shapes in the layout of content that is about to arrive; put aria-busy on the region it fills. */
export function skeleton(p: SkeletonProps = {}): TemplateResult {
  const v = p.variant || 'text';
  let shapes: unknown;
  if (v === 'block') {
    const height = p.height != null ? declaration('height', dim(p.height)) : undefined;
    shapes = html`<span class="sdr-skeleton__bone sdr-skeleton__bone--block" style=${ifDefined(height)}></span>`;
  } else if (v === 'metric') shapes = [bone('44%', 'label'), bone('56%', 'figure'), bone('36%', 'caption')];
  else if (v === 'table') {
    const cols = Math.max(1, p.columns || 4);
    const rows = p.rows || 5;
    const grid = `grid-template-columns:${cols > 1 ? `1.6fr repeat(${cols - 1}, 1fr)` : '1fr'}`;
    const cellClass = (c: number) => cx('sdr-skeleton__cell', cols > 2 && c === cols - 1 && 'is-num');
    shapes = [
      html`<span class="sdr-skeleton__row sdr-skeleton__row--head" style=${grid}>${range(cols).map(
        c => html`<span class=${cellClass(c)}>${bone(c ? '40%' : '28%', 'label')}</span>`,
      )}</span>`,
      ...range(rows).map(
        r =>
          html`<span class="sdr-skeleton__row" style=${grid}>${range(cols).map(
            c =>
              html`<span class=${cellClass(c)}>${bone(
                c ? CELL[(r + c) % CELL.length] : CELL0[r % CELL0.length],
                c ? 'caption' : 'line',
              )}</span>`,
          )}</span>`,
      ),
    ];
  } else {
    const n = Math.max(1, p.lines || 3);
    shapes = range(n).map(i => bone(n > 1 && i === n - 1 ? '62%' : LINE[i % LINE.length], 'line'));
  }
  const named = !!p.label;
  const width = p.width != null ? declaration('width', dim(p.width)) : undefined;
  return html`<div
    class=${cx('sdr-skeleton', `sdr-skeleton--${v}`, v === 'table' && p.density === 'compact' && 'sdr-skeleton--compact', p.className)}
    style=${ifDefined(width)}
    aria-hidden=${ifDefined(named ? undefined : 'true')}
  >${named ? html`<span class="sdr-visually-hidden">${node(p.label)}</span>` : nothing}<div
      class="sdr-skeleton__shapes"
      aria-hidden=${ifDefined(named ? 'true' : undefined)}
    >${shapes}</div></div>`;
}

// ---- EmptyState ----

/** No icon: an empty state is words and one action (I7, S18). */
const EMPTY_VARIANTS = new Set(['first-run', 'no-results', 'error']);

/** Title and body as one announcement, each part ending as a sentence. */
function sentences(parts: string[]): string {
  return parts
    .map(s => s.trim())
    .filter(Boolean)
    .map(s => (/[.!?:…]$/.test(s) ? s : `${s}.`))
    .join(' ');
}

/** What the system's effect watches: the variant and the title and body, as far as they are text or numbers. */
const plainWords = (...parts: unknown[]) =>
  parts.map(x => (typeof x === 'string' || typeof x === 'number' ? String(x) : '')).join('|');

export class SdrEmptyState extends LitElement {
  static override properties = {
    variant: {},
    heading: { attribute: false },
    content: { attribute: false },
    action: { attribute: false },
    onRetry: { attribute: false },
    retryLabel: {},
    retrying: { type: Boolean },
    level: { type: Number },
    size: {},
    emptyClass: { attribute: false },
    heard: { state: true },
  };

  /** first-run (the default), no-results (announced politely) or error (role alert, Try again). */
  declare variant: 'first-run' | 'no-results' | 'error' | undefined;
  /** One sentence, the fact: "No campaigns yet". */
  declare heading: unknown;
  /** The body: why, and what happens next. */
  declare content: unknown;
  /** The one action, usually a Button. */
  declare action: unknown;
  /** error: shows Try again when no action is given. Called with nothing. */
  declare onRetry: (() => void) | undefined;
  declare retryLabel: string | undefined;
  /** error: Try again spins and waits; the focus waits on the state. */
  declare retrying: boolean | undefined;
  /** Heading level of the title (default 3). */
  declare level: 2 | 3 | 4 | 5 | 6 | undefined;
  /** sm: inside a panel or table (32px padding); md: a page (48px). */
  declare size: 'sm' | 'md' | undefined;
  declare emptyClass: string | undefined;
  protected declare heard: { n: number; text: string };

  private readonly box = createRef<HTMLElement>();
  /** The system's back ref: Try again was pressed with the focus in the state. */
  private back = false;
  /** What the announcement last started for (the effect's dependencies); undefined before it runs. */
  private armedFor: string | undefined;
  private say: ReturnType<typeof setTimeout> | undefined;
  private hush: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    super();
    this.heard = { n: 0, text: '' };
  }

  override createRenderRoot() {
    return this;
  }

  private get kind(): string {
    const v = this.variant as string;
    return v && EMPTY_VARIANTS.has(v) ? v : 'first-run';
  }

  override connectedCallback() {
    super.connectedCallback();
    noBox(this);
    // Back on the page: as at a first mount, nothing said yet, and the announcement starts again.
    if (this.hasUpdated) {
      this.back = false;
      this.heard = { n: this.heard.n, text: '' };
      this.requestUpdate();
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.disarm();
    this.armedFor = undefined;
  }

  private disarm() {
    clearTimeout(this.say);
    clearTimeout(this.hush);
    this.say = this.hush = undefined;
  }

  protected override updated(changed: PropertyValues<this>) {
    super.updated(changed);
    // Off the page nothing is said and nothing waits: a redraw there starts no timer.
    if (!this.isConnected) return;
    const v = this.kind;
    const deps = `${v}\u0000${v === 'no-results' ? plainWords(this.heading, this.content) : ''}`;
    if (deps !== this.armedFor) {
      this.armedFor = deps;
      this.disarm();
      if (v === 'no-results') {
        this.say = setTimeout(() => {
          const el = this.box.value;
          if (!el) return;
          const text = sentences(
            [el.querySelector('.sdr-empty__title'), el.querySelector('.sdr-empty__body')].map(n =>
              n ? (n.textContent ?? '') : '',
            ),
          );
          this.heard = { n: this.heard.n + 1, text };
        }, 150);
        this.hush = setTimeout(() => {
          this.heard = { n: this.heard.n, text: '' };
        }, 7000);
      }
    }
    // Try again goes busy (disabled) while it runs: keep the focus here, then hand it back if it failed again.
    if (changed.has('retrying')) {
      const el = this.box.value;
      if (!el || !this.back) return;
      if (this.retrying) {
        if (focusLost()) el.focus();
        return;
      }
      this.back = false;
      const b = el.querySelector<HTMLElement>('.sdr-empty__action button:not([disabled])');
      if (b && (focusLost() || document.activeElement === el)) b.focus();
    }
  }

  private readonly retry = () => {
    const el = this.box.value;
    this.back = !!el?.contains(document.activeElement);
    this.onRetry?.();
  };

  override render() {
    const v = this.kind;
    const level = Math.min(6, Math.max(2, this.level || 3));
    const tag = unsafeStatic(`h${level}`);
    // Lit's nothing is the app's null: Try again stays (present() in helpers.ts).
    const action =
      this.action != null && this.action !== nothing
        ? this.action
        : v === 'error' && this.onRetry
          ? button(
              { variant: 'secondary', loading: this.retrying, onClick: this.retry },
              this.retryLabel || 'Try again',
            )
          : null;
    return html`<div
      ${ref(this.box)}
      tabindex="-1"
      class=${cx('sdr-empty', `sdr-empty--${v}`, this.size === 'sm' && 'sdr-empty--sm', this.emptyClass)}
      role=${ifDefined(v === 'error' ? 'alert' : undefined)}
    >
      ${staticHtml`<${tag} class="sdr-empty__title">${node(this.heading)}</${tag}>`}
      ${present(this.content) ? html`<div class="sdr-empty__body">${node(this.content)}</div>` : nothing}
      ${present(action) ? html`<div class="sdr-empty__action">${node(action)}</div>` : nothing}
      ${
        v === 'no-results'
          ? html`<span class="sdr-visually-hidden" role="status">${
              this.heard.text ? keyed(this.heard.n, html`<span>${this.heard.text}</span>`) : nothing
            }</span>`
          : nothing
      }
    </div>`;
  }
}

// ---- SaveStatus ----

const SAVE_TEXT: Record<string, string> = {
  idle: '',
  saving: 'Saving…',
  saved: 'Saved',
  unsaved: 'Unsaved changes',
  failed: 'Changes not saved',
};

/** Times read 14:05, as the brand book asks, in every locale. */
const CLOCK: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };

/** When it was saved, from now: "just now", "4 min ago", "at 14:05", "on Oct 27, 14:05". */
function ago(d: Date | string | number): string {
  const t = asDate(d);
  const s = (Date.now() - t.getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${num(Math.floor(s / 60))} min ago`;
  if (t.toDateString() === new Date().toDateString()) return `at ${format.time(t, CLOCK)}`;
  return `on ${format.date(t, { day: 'numeric', month: 'short' })}, ${format.time(t, CLOCK)}`;
}

export type SaveState = 'idle' | 'saving' | 'saved' | 'unsaved' | 'failed';

export class SdrSaveStatus extends LitElement {
  static override properties = {
    state: {},
    savedAt: { attribute: false },
    error: {},
    onRetry: { attribute: false },
    retryLabel: {},
    text: {},
    saveClass: { attribute: false },
    said: { state: true },
    tick: { state: true },
  };

  declare state: SaveState | undefined;
  /** saved: when, shown relative and kept current. */
  declare savedAt: Date | string | number | undefined;
  /** failed: the words, written as the fix. Default "Changes not saved". */
  declare error: string | undefined;
  /** failed: shows Retry. Called with nothing. */
  declare onRetry: (() => void) | undefined;
  declare retryLabel: string | undefined;
  /** Replaces the state's words. */
  declare text: string | undefined;
  declare saveClass: string | undefined;
  protected declare said: { n: number; text: string };
  protected declare tick: number;

  private readonly box = createRef<HTMLElement>();
  /** The system's back ref: Retry was pressed with the focus in the status. */
  private back = false;
  /** The state and failure words the announcement effect last ran with (the system's prev ref). */
  private prev: { state: string; fail: string } | undefined;
  /** What the interval last started for (state and savedAt); null before it runs. */
  private everyFor: [string, unknown] | null = null;
  private every: ReturnType<typeof setInterval> | undefined;
  private quiet: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    super();
    this.said = { n: 0, text: '' };
    this.tick = 0;
  }

  override createRenderRoot() {
    return this;
  }

  /** The state shown: one the system knows, else idle. */
  get current(): string {
    const s = this.state as string;
    return SAVE_TEXT[s] != null ? s : 'idle';
  }

  private get failText(): string {
    return this.error || (SAVE_TEXT.failed as string);
  }

  override connectedCallback() {
    super.connectedCallback();
    noBox(this);
    // Back on the page: as at a first mount, nothing said yet, the state taken as it is, the interval again.
    if (this.hasUpdated) {
      this.back = false;
      this.prev = undefined;
      this.said = { n: this.said.n, text: '' };
      this.requestUpdate();
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.every);
    clearTimeout(this.quiet);
    this.every = this.quiet = undefined;
    this.everyFor = null;
  }

  protected override updated(changed: PropertyValues<this>) {
    super.updated(changed);
    // Off the page nothing is said and nothing ticks: a redraw there starts no timer.
    if (!this.isConnected) return;
    const state = this.current;
    const failText = this.failText;

    // Kept current: draw again every 30 s while saved.
    if (!this.everyFor || this.everyFor[0] !== state || this.everyFor[1] !== this.savedAt) {
      this.everyFor = [state, this.savedAt];
      clearInterval(this.every);
      this.every = undefined;
      if (state === 'saved' && this.savedAt != null)
        this.every = setInterval(() => {
          this.tick = this.tick + 1;
        }, 30000);
    }

    // Each announcement is a new node (n), so the same words twice (failed, Retry, failed again) are read twice.
    const was = this.prev ?? { state, fail: failText };
    if (this.prev && was.state === state && was.fail === failText) return;
    this.prev = { state, fail: failText };
    const moved = was.state !== state;
    const msg =
      state === 'saved' && moved
        ? (SAVE_TEXT.saved as string)
        : state === 'failed' && (moved || was.fail !== failText)
          ? failText
          : '';
    if (msg) {
      this.said = { n: this.said.n + 1, text: msg };
      clearTimeout(this.quiet);
      this.quiet = setTimeout(() => {
        this.said = { n: this.said.n, text: '' };
      }, 5000);
    }
    if (!moved) return;
    // Retry disappears while saving: the focus waits here, and goes back to Retry if the save failed again.
    const el = this.box.value;
    if (!el || !this.back) return;
    if (state === 'failed') {
      this.back = false;
      const b = el.querySelector<HTMLElement>('button');
      if (b && (focusLost() || document.activeElement === el)) b.focus();
    } else {
      if (focusLost()) el.focus();
      if (state !== 'saving') this.back = false;
    }
  }

  private readonly retry = () => {
    const el = this.box.value;
    this.back = !!el?.contains(document.activeElement);
    this.onRetry?.();
  };

  override render() {
    const state = this.current;
    const failText = this.failText;
    const lead =
      state === 'saving'
        ? html`<span class="sdr-spinner" aria-hidden="true"></span>`
        : state === 'saved'
          ? icon({ name: 'check', size: 14 })
          : state === 'unsaved'
            ? html`<span class="sdr-save__dot" aria-hidden="true"></span>`
            : state === 'failed'
              ? icon({ name: 'alert', size: 14 })
              : nothing;
    const text = this.text || (state === 'failed' ? failText : SAVE_TEXT[state]);
    const at = state === 'saved' && this.savedAt != null ? asDate(this.savedAt) : null;
    const time = at
      ? html` <time
          class="sdr-save__time"
          datetime=${at.toISOString()}
          title=${`${format.date(at)}, ${format.time(at, CLOCK)}`}
        >${ago(at)}</time>`
      : nothing;
    return html`<span ${ref(this.box)} tabindex="-1" class=${cx('sdr-save', `sdr-save--${state}`, this.saveClass)}
      >${text ? html`<span class="sdr-save__state">${lead}<span>${node(text)}${time}</span></span>` : nothing}${
        state === 'failed' && this.onRetry
          ? button({ size: 'sm', onClick: this.retry }, this.retryLabel || 'Retry')
          : nothing
      }<span class="sdr-visually-hidden" role="status">${
        this.said.text ? keyed(this.said.n, html`<span>${this.said.text}</span>`) : nothing
      }</span></span
    >`;
  }
}

if (!customElements.get('sdr-empty-state')) customElements.define('sdr-empty-state', SdrEmptyState);
if (!customElements.get('sdr-save-status')) customElements.define('sdr-save-status', SdrSaveStatus);

declare global {
  interface HTMLElementTagNameMap {
    'sdr-empty-state': SdrEmptyState;
    'sdr-save-status': SdrSaveStatus;
  }
}
